import {
  DefinitionsFromApi,
  OverrideResultType,
  TagTypesFromApi,
} from "@reduxjs/toolkit/query";
import { Links, parseLinkHeader } from "@web3-storage/parse-link-header";
import { toSafeInteger } from "lodash-es";

import {
  GridFilterModel,
  GridLogicOperator,
  GridSortModel,
} from "@mui/x-data-grid";

import { type RootState, useSelector } from "./index";
import {
  ActivityLog,
  Changeset,
  ClusterFlavor,
  GetSettingsApiArg,
  KeyPairWithCertificateInfo,
  ListActivityLogsOrderByField,
  ListActivityLogsResponse,
  ListChangesetsOrderByField,
  ListChangesetsResponse,
  ListKeyPairsOrderByField,
  ListKeyPairsResponse,
  ListPoliciesOrderByField,
  ListPoliciesResponse,
  ListRoutesOrderByField,
  ListRoutesResponse,
  Policy,
  Route,
  pomeriumApi as api,
  useGetSettingsQuery as useGetSettingsQueryRaw,
} from "./pomeriumApi";

type PagedResult<T> = {
  rows: T[];
  rowCount: number;
  estimatedRowCount: number;
};

function getPagedResult<T>(
  rows: T[],
  links: Links | null,
  total: number,
): PagedResult<T> {
  return isNaN(total)
    ? links?.next
      ? {
          rows: rows,
          rowCount: -1,
          estimatedRowCount: toSafeInteger(links?.last?.offset),
        }
      : {
          rows: rows,
          rowCount: toSafeInteger(links?.last?.offset) + rows.length,
          estimatedRowCount: -1,
        }
    : {
        rows: rows,
        rowCount: total,
        estimatedRowCount: -1,
      };
}

type Definitions = DefinitionsFromApi<typeof api>;
type TagTypes = TagTypesFromApi<typeof api>;
type UpdatedDefinitions = Omit<
  Definitions,
  | "listActivityLogs"
  | "listChangesets"
  | "listKeyPairs"
  | "listPolicies"
  | "listRoutes"
> & {
  listActivityLogs: OverrideResultType<
    Definitions["listActivityLogs"],
    PagedResult<ActivityLog>
  >;
  listChangesets: OverrideResultType<
    Definitions["listChangesets"],
    PagedResult<Changeset>
  >;
  listKeyPairs: OverrideResultType<
    Definitions["listKeyPairs"],
    PagedResult<KeyPairWithCertificateInfo>
  >;
  listPolicies: OverrideResultType<
    Definitions["listPolicies"],
    PagedResult<Policy>
  >;
  listRoutes: OverrideResultType<Definitions["listRoutes"], PagedResult<Route>>;
};

export const enhancedApi = api.enhanceEndpoints<TagTypes, UpdatedDefinitions>({
  endpoints: {
    listActivityLogs: {
      transformResponse(
        response: ListActivityLogsResponse,
        meta,
      ): PagedResult<ActivityLog> {
        return getPagedResult(
          response,
          parseLinkHeader(meta?.response?.headers?.get("Link") || ""),
          parseInt(meta?.response?.headers?.get("X-Total-Count") || ""),
        );
      },
    },
    listChangesets: {
      transformResponse(
        response: ListChangesetsResponse,
        meta,
      ): PagedResult<Changeset> {
        return getPagedResult(
          response,
          parseLinkHeader(meta?.response?.headers?.get("Link") || ""),
          parseInt(meta?.response?.headers?.get("X-Total-Count") || ""),
        );
      },
    },
    listKeyPairs: {
      transformResponse(
        response: ListKeyPairsResponse,
        meta,
      ): PagedResult<KeyPairWithCertificateInfo> {
        return getPagedResult(
          response,
          parseLinkHeader(meta?.response?.headers?.get("Link") || ""),
          parseInt(meta?.response?.headers?.get("X-Total-Count") || ""),
        );
      },
    },
    listPolicies: {
      transformResponse(
        response: ListPoliciesResponse,
        meta,
      ): PagedResult<Policy> {
        return getPagedResult(
          response,
          parseLinkHeader(meta?.response?.headers?.get("Link") || ""),
          parseInt(meta?.response?.headers?.get("X-Total-Count") || ""),
        );
      },
    },
    listRoutes: {
      transformResponse(
        response: ListRoutesResponse,
        meta,
      ): PagedResult<Route> {
        return getPagedResult(
          response,
          parseLinkHeader(meta?.response?.headers?.get("Link") || ""),
          parseInt(meta?.response?.headers?.get("X-Total-Count") || ""),
        );
      },
    },
  },
});

export const {
  useListActivityLogsQuery,
  useListChangesetsQuery,
  useListKeyPairsQuery,
  useListPoliciesQuery,
  useListRoutesQuery,
} = enhancedApi;

/**
 * A hook that wraps useGetSettingsQueryRaw and conditionally skips the query
 * based on the cluster flavor.
 *
 * This skips the getSettings query for hosted clusters, as those clusters
 * don't support the settings endpoint.
 *
 * @param arg - The API arguments (organizationId and clusterId)
 * @param options - Query options including skip flag and optional clusterFlavor.
 *   If clusterFlavor is provided, it will be used directly. Otherwise, the flavor
 *   will be read from Redux state. Passing clusterFlavor explicitly is recommended
 *   to avoid race conditions when switching clusters.
 * @returns The result of the useGetSettingsQueryRaw hook, with skip logic applied.
 */
export function useGetSettingsQuery(
  arg: GetSettingsApiArg,
  options?: { skip?: boolean; clusterFlavor?: ClusterFlavor } & Record<
    string,
    unknown
  >,
) {
  const flavorFromState = useSelector(
    (state: RootState) => state.current.flavor,
  );

  const flavor = options?.clusterFlavor ?? flavorFromState;

  const skip =
    options?.skip ||
    !arg.organizationId ||
    !arg.clusterId ||
    flavor === "hosted";

  return useGetSettingsQueryRaw(arg, {
    ...options,
    skip,
  });
}

type OrderByField =
  | ListActivityLogsOrderByField
  | ListChangesetsOrderByField
  | ListKeyPairsOrderByField
  | ListPoliciesOrderByField
  | ListRoutesOrderByField;
export function getOrderBy<T extends OrderByField>(
  sortModel: GridSortModel,
): T[] | undefined {
  const fields = sortModel.flatMap(({ field, sort }): T[] => [
    `${sort === "desc" ? "-" : ""}${field}` as T,
  ]);
  return fields.length > 0 ? fields : undefined;
}

export function getFilter(filterModel: GridFilterModel): string | undefined {
  const items = filterModel?.items?.filter((i) => i.value !== undefined) || [];
  const quickFilterValues =
    filterModel?.quickFilterValues?.filter((i) => !!i)?.map((i) => `${i}`) ||
    [];
  if (items.length > 0 || quickFilterValues.length > 0) {
    return JSON.stringify({
      items: items.map((i) => ({
        field: i.field,
        operator: i.operator,
        value: i.value,
      })),
      logicOperator:
        filterModel?.logicOperator === GridLogicOperator.And ? "and" : "or",
      quickFilterValues: quickFilterValues,
      quickFilterLogicOperator:
        filterModel?.quickFilterLogicOperator === GridLogicOperator.And
          ? "and"
          : "or",
    });
  }
  return undefined;
}
